'use client'

import * as React from 'react'
import { cn } from '../helpers/index.js'
import type { ComponentPropsWithout, RemovedProps } from '../helpers/component-props.js'
import { cva, type VariantProps } from 'class-variance-authority'

const inputVariants = cva(
  'border-input text-info-primary bg-background flex w-full flex-row items-center overflow-hidden rounded-md border focus-visible:outline-none',
  {
    variants: {
      size: {
        sm: 'h-8 text-sm',
        base: 'h-10',
        lg: 'h-12 text-lg',
      },
    },
    defaultVariants: {
      size: 'base',
    },
  }
)

export interface InputProps extends ComponentPropsWithout<'input', 'size'>, VariantProps<typeof inputVariants> {}

type InputSlotElement = React.ElementRef<'div'>
interface InputSlotProps extends ComponentPropsWithout<'div', RemovedProps> {
  /**
   * 插槽的位置
   * default: right
   */
  side?: 'left' | 'right'
}

/**
 * InputSlot
 * 用来在 Input 组件中插入额外的内容，比如icon ，按钮
 */
const InputSlot = React.forwardRef<InputSlotElement, InputSlotProps>((props, forwardedRef) => {
  const { className, side = 'right', children, ...slotProps } = props

  return (
    <div
      ref={forwardedRef}
      {...slotProps}
      className={cn('flex h-full items-center', className, {
        '-order-1': side === 'left',
        'order-2': side === 'right',
      })}
    >
      {children}
    </div>
  )
})

/**
 * Input - 输入框
 *
 * @description 文本输入组件，支持各种输入类型、不同尺寸和插槽功能
 */
const Input = React.forwardRef<React.ElementRef<'input'>, InputProps>(
  ({ className, type, size, children, ...props }, ref) => {
    if (!React.isValidElement(children)) {
      return <input type={type} className={cn(inputVariants({ size }), 'px-3', className)} ref={ref} {...props} />
    }
    return (
      <div className={cn(inputVariants({ size }), className)}>
        <input
          type={type}
          className={cn(
            'placeholder:text-muted-foreground flex size-full px-3 file:border-0 file:bg-transparent file:font-medium focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
          )}
          ref={ref}
          {...props}
        />
        {children}
      </div>
    )
  }
)
Input.displayName = 'Input'
InputSlot.displayName = 'InputSlot'

export { Input, InputSlot }
